import type { Plugin } from 'chart.js';

function darkenColor(color: string, factor: number): string {
  if (color.startsWith('#') && color.length >= 7) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    const f = 1 - factor;
    return `rgb(${Math.round(r * f)},${Math.round(g * f)},${Math.round(b * f)})`;
  }
  const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgba) {
    const f = 1 - factor;
    return `rgb(${Math.round(Number(rgba[1]) * f)},${Math.round(Number(rgba[2]) * f)},${Math.round(Number(rgba[3]) * f)})`;
  }
  return color;
}

type ArcLike = {
  hidden?: boolean;
  x: number;
  y: number;
  startAngle: number;
  endAngle: number;
  innerRadius: number;
  outerRadius: number;
};

/** Pseudo-3D depth for pie charts (shadow + extruded sides + offset base). */
export const pie3dPlugin: Plugin<'pie'> = {
  id: 'pie3d',
  beforeDatasetDraw(chart, args, options) {
    const depth = typeof options?.depth === 'number' ? options.depth : 16;
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(args.index);
    const dataset = chart.data.datasets[args.index];
    const colors = dataset.backgroundColor;
    if (!Array.isArray(colors) || !meta.data.length) return;

    const first = meta.data[0] as unknown as ArcLike;
    const { x, y, outerRadius } = first;

    ctx.save();
    ctx.fillStyle = 'rgba(15, 23, 42, 0.12)';
    ctx.beginPath();
    ctx.ellipse(x, y + depth + outerRadius * 0.08, outerRadius * 0.92, outerRadius * 0.22, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.save();
    meta.data.forEach((element, i) => {
      const arc = element as unknown as ArcLike;
      if (!arc || arc.hidden) return;

      const { x, y, startAngle, endAngle, innerRadius, outerRadius } = arc;
      const baseColor = colors[i] ?? '#888';
      const sideColor = darkenColor(String(baseColor), 0.28);
      const edgeColor = darkenColor(String(baseColor), 0.42);

      ctx.fillStyle = sideColor;
      ctx.beginPath();
      ctx.arc(x, y + depth, outerRadius, startAngle, endAngle);
      ctx.arc(x, y + depth, innerRadius, endAngle, startAngle, true);
      ctx.closePath();
      ctx.fill();

      for (const angle of [startAngle, endAngle]) {
        ctx.fillStyle = edgeColor;
        ctx.beginPath();
        ctx.moveTo(x + Math.cos(angle) * innerRadius, y + Math.sin(angle) * innerRadius);
        ctx.lineTo(x + Math.cos(angle) * outerRadius, y + Math.sin(angle) * outerRadius);
        ctx.lineTo(x + Math.cos(angle) * outerRadius, y + depth + Math.sin(angle) * outerRadius);
        ctx.lineTo(x + Math.cos(angle) * innerRadius, y + depth + Math.sin(angle) * innerRadius);
        ctx.closePath();
        ctx.fill();
      }
    });
    ctx.restore();
  },
};
